// Property Analysis Types and Interfaces

export type PropertySource = 'redfin' | 'zillow';

export type AnalysisToolType = 
  | 'propertyParser'
  | 'cma'
  | 'neighborhood'
  | 'pricing'
  | 'negotiation'
  | 'floorPlan'
  | 'listingDescription'
  | 'clientConsultation'
  | 'leadQualification'
  | 'transactionManagement'
  | 'preTourPrep'
  | 'clientMatch'
  | 'buyerSentiment'
  | 'agentOrchestrator';

export type AnalysisStatus = 'pending' | 'in_progress' | 'completed' | 'error';

// API Request Types
export interface PropertyAnalysisConfig {
  includeAgents: {
    propertyParser: boolean;
    cma: boolean;
    neighborhood: boolean;
    pricing: boolean;
    negotiation: boolean;
    floorPlan: boolean;
    listingDescription: boolean;
    clientConsultation: boolean;
    leadQualification: boolean;
    transactionManagement: boolean;
    preTourPrep: boolean;
    clientMatch: boolean;
    buyerSentiment: boolean;
    agentOrchestrator: boolean;
  };
  timeout: number;
  retryAttempts: number;
}

export interface PropertyAnalysisRequest {
  url: string;
  source: PropertySource;
  email: string;
  config: PropertyAnalysisConfig;
}

// Property Data Types
export interface PropertyCoordinates {
  latitude: number;
  longitude: number;
}

export interface PropertyAgent {
  name: string;
  phone: string;
  dreLicense?: string;
}

export interface PropertyHOA {
  fee: number;
  frequency: string;
  name: string;
}

export interface PropertyFeatures {
  interior: string[];
  exterior: string[];
  appliances: string[];
}

export interface PropertyPhotos {
  count: number;
  types: string[];
}

export interface Property {
  address: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFootage: number;
  propertyType: string;
  listingStatus: string;
  condition: string;
  yearBuilt: number;
  lotSize?: number;
  pricePerSqFt: number;
  mlsNumber?: string;
  description?: string;
  coordinates: PropertyCoordinates;
  agent: PropertyAgent;
  hoa?: PropertyHOA;
  features: PropertyFeatures;
  schools: string[];
  photos: PropertyPhotos;
}

// Verification Types
export interface PropertyVerification {
  confidence: number;
  validationChecks: string[];
  dataQuality: 'high' | 'medium' | 'low';
  missingFields: string[];
  suggestions: string[];
  inconsistencies: string[];
}

// Analysis Result Types
export interface PropertyParserResult {
  property: Property;
  verification: PropertyVerification;
  rawData: Record<string, any>;
  processingTime: number;
}

export interface PriceRange {
  min: number;
  max: number;
  optimal?: number;
}

export interface MarketComparable {
  address: string;
  price: number;
  pricePerSqFt: number;
  bedrooms: number;
  bathrooms: number;
  squareFootage: number;
  daysOnMarket: number;
  status: 'sold' | 'active' | 'pending';
  advantages: string[];
  disadvantages: string[];
}

export interface PricingAnalysisResult {
  marketPositioning: {
    priceSegment: string;
    competitivePosition: string;
    targetBuyerProfile: {
      primary: string;
      secondary: string;
      demographics: string[];
      motivations: string[];
    };
    uniqueValueProposition: string[];
    marketDifferentiators: string[];
  };
  pricingStrategy: {
    recommendedPrice: number;
    priceRange: PriceRange;
    pricingApproach: string;
    priceJustification: {
      marketComparables: string[];
      propertyFeatures: string[];
      locationAdvantages: string[];
      timingFactors: string[];
    };
    pricingConfidence: number;
  };
  competitiveAnalysis: {
    directCompetitors: MarketComparable[];
    marketGaps: string[];
    competitiveAdvantages: string[];
    competitiveDisadvantages: string[];
    positioningOpportunities: string[];
  };
  marketTiming: {
    optimalListingTime: string;
    seasonalFactors: Record<string, number>;
    marketCycle: string;
    timingRecommendations: string[];
    urgencyFactors: string[];
    patienceFactors: string[];
  };
  recommendations: {
    immediate: string[];
    shortTerm: string[];
    longTerm: string[];
  };
  riskAssessment: {
    overpricing: number;
    underpricing: number;
    marketRisk: number;
    timingRisk: number;
  };
  confidence: number;
  dataQuality: string;
  lastUpdated: string;
}

export interface CMAResult {
  cmaAnalysis: {
    subjectProperty: {
      estimatedValue: number;
      valueRange: PriceRange;
      confidence: number;
      pricePerSqFt: number;
      marketPosition: string;
    };
    comparables: MarketComparable[];
    marketTrends: {
      averagePrice: number;
      averagePricePerSqFt: number;
      medianPrice: number;
      priceRange: PriceRange;
      daysOnMarket: {
        average: number;
        median: number;
      };
      inventoryLevel: string;
      marketDirection: string;
      seasonality: {
        currentSeason: string;
        seasonalFactor: number;
      };
    };
    competitiveAnalysis: {
      advantages: string[];
      disadvantages: string[];
      uniqueFeatures: string[];
      pricingStrategy: string;
      recommendedPrice: number;
      negotiationRange: PriceRange;
    };
    marketInsights: {
      demandFactors: string[];
      supplyFactors: string[];
      externalFactors: string[];
      riskFactors: string[];
    };
    recommendations: {
      pricing: string[];
      marketing: string[];
      timing: string[];
      improvements: string[];
    };
  };
  confidence: number;
  dataQuality: string;
  missingData: string[];
  lastUpdated: string;
}

export interface FloorPlanResult {
  floorPlanAnalysis: {
    levels: Array<{
      level: number;
      squareFootage: number;
      verifiedSquareFootage: number;
      confidence: number;
      rooms: Array<{
        type: string;
        features: string[];
        location: string;
        premiumFeatures: string[];
      }>;
      layout: {
        openConcept: boolean;
        flow: string;
        accessibility: string;
        entertainmentFlow: string;
      };
    }>;
    roomFlowAssessment: {
      overallFlow: string;
      trafficPatterns: string[];
      optimizationSuggestions: string[];
    };
    featureIdentification: {
      premiumFeatures: string[];
      uniqueFeatures: string[];
      valueAdditions: string[];
    };
    squareFootageVerification: {
      listedSqFt: number;
      calculatedSqFt: number;
      difference: number;
      confidence: number;
      recommendations: string[];
    };
  };
  confidence: number;
  dataQuality: string;
  missingFields: string[];
  suggestions: string[];
}

export interface NeighborhoodResult {
  neighborhoodIntelligence: {
    demographics: {
      population: number;
      medianAge: number;
      medianIncome: number;
      educationLevel: {
        highSchool: number;
        bachelors: number;
        graduate: number;
      };
      householdSize: number;
      homeownershipRate: number;
      diversityIndex: number;
      employmentRate: number;
    };
    amenities: {
      schools: {
        elementary: number;
        middle: number;
        high: number;
        private: number;
        rating: number;
      };
      healthcare: {
        hospitals: number;
        clinics: number;
        pharmacies: number;
        urgentCare: number;
      };
      transportation: {
        publicTransit: number;
        bikeLanes: number;
        walkability: number;
        parking: number;
      };
      shopping: {
        groceryStores: number;
        restaurants: number;
        retail: number;
        malls: number;
      };
      recreation: {
        parks: number;
        gyms: number;
        libraries: number;
        entertainment: number;
      };
      safety: {
        crimeRate: number;
        policeStations: number;
        fireStations: number;
        safetyScore: number;
      };
    };
    marketContext: {
      priceTrends: {
        oneYear: number;
        threeYear: number;
        fiveYear: number;
        forecast: number;
      };
      inventoryTrends: {
        current: number;
        average: number;
        turnover: number;
      };
      demandIndicators: {
        daysOnMarket: number;
        priceReductions: number;
        multipleOffers: number;
      };
      investmentPotential: {
        rentalYield: number;
        appreciation: number;
        development: number;
      };
    };
    neighborhoodScore: {
      overall: number;
      livability: number;
      investment: number;
      family: number;
      professional: number;
    };
    insights: {
      strengths: string[];
      weaknesses: string[];
      opportunities: string[];
      threats: string[];
    };
    recommendations: {
      forBuyers: string[];
      forSellers: string[];
      forInvestors: string[];
      forFamilies: string[];
    };
  };
  confidence: number;
  dataQuality: string;
  missingData: string[];
  lastUpdated: string;
}

export interface NegotiationResult {
  buyerPsychology: {
    buyerType: string;
    motivationLevel: string;
    urgencyFactors: string[];
    decisionDrivers: string[];
    emotionalTriggers: string[];
    riskTolerance: string;
    negotiationStyle: string;
    priceSensitivity: string;
    timelinePressure: string;
  };
  negotiationStrategy: {
    approach: string;
    initialPosition: {
      price: number;
      terms: string[];
      concessions: string[];
    };
    targetPosition: {
      price: number;
      terms: string[];
      mustHaves: string[];
      niceToHaves: string[];
    };
    walkAwayPosition: {
      price: number;
      terms: string[];
      dealBreakers: string[];
    };
    concessionStrategy: {
      order: string[];
      timing: string[];
      value: number[];
    };
    counterOfferStrategy: {
      frequency: string;
      increments: number[];
      justifications: string[];
    };
  };
  marketLeverage: {
    sellerAdvantages: string[];
    buyerAdvantages: string[];
    marketConditions: string;
    inventoryPressure: string;
    competitionLevel: string;
    timeAdvantage: string;
    leverageFactors: {
      property: string[];
      market: string[];
      timing: string[];
      personal: string[];
    };
  };
  dealOptimization: {
    optimalTerms: {
      price: number;
      closingDate: string;
      contingencies: string[];
      financing: string[];
      repairs: string[];
      inclusions: string[];
    };
    valueEnhancements: {
      immediate: string[];
      shortTerm: string[];
      longTerm: string[];
    };
    riskMitigation: {
      contingencies: string[];
      protections: string[];
      alternatives: string[];
    };
    closingOptimization: {
      timeline: string[];
      requirements: string[];
      strategies: string[];
    };
  };
  communicationStrategy: {
    messaging: {
      primary: string[];
      secondary: string[];
      emotional: string[];
      rational: string[];
    };
    timing: {
      responseTime: string;
      followUp: string;
      pressure: string;
    };
    channels: {
      preferred: string[];
      backup: string[];
      avoid: string[];
    };
    objections: {
      common: string[];
      responses: string[];
      strategies: string[];
    };
  };
  recommendations: {
    immediate: string[];
    preparation: string[];
    execution: string[];
    followUp: string[];
  };
  riskAssessment: {
    dealFailure: number;
    valueLoss: number;
    timelineRisk: number;
    relationshipRisk: number;
  };
  confidence: number;
  dataQuality: string;
  lastUpdated: string;
}

// Complete Analysis Response
export interface PropertyAnalysisData {
  property: {
    url: string;
    source: PropertySource;
    extractionData: PropertyParserResult;
  };
  aiAnalysis: {
    propertyParser?: PropertyParserResult;
    pricing?: PricingAnalysisResult;
    floorPlan?: FloorPlanResult;
    cma?: CMAResult;
    negotiation?: NegotiationResult;
    neighborhood?: NeighborhoodResult;
    listingDescription?: any; // TODO: Define proper type
    clientConsultation?: any; // TODO: Define proper type
    leadQualification?: any; // TODO: Define proper type
    transactionManagement?: any; // TODO: Define proper type
    preTourPrep?: any; // TODO: Define proper type
    clientMatch?: any; // TODO: Define proper type
    buyerSentiment?: any; // TODO: Define proper type
    agentOrchestrator?: any; // TODO: Define proper type
  };
  summary: {
    confidence: number;
    dataQuality: string;
    recommendedPrice?: number;
    marketPosition?: string;
    neighborhoodScore?: number;
    insights: {
      priceRange?: PriceRange;
      marketTrends?: any;
      recommendations: string[];
    };
  };
  processingTime: number;
  timestamp: string;
  analysisId: string;
}

export interface PropertyAnalysisResponse {
  success: boolean;
  data: PropertyAnalysisData;
  processingTime: number;
  timestamp: string;
}

// State Management Types
export interface AnalysisProgress {
  [toolName: string]: {
    status: AnalysisStatus;
    progress: number; // 0-100
    estimatedTime?: number;
    error?: string;
  };
}

export interface PropertyAnalysisState {
  activeAnalysis: {
    id: string;
    url: string;
    source: PropertySource;
    selectedTools: AnalysisToolType[];
    status: AnalysisStatus;
    progress: AnalysisProgress;
    results?: PropertyAnalysisData;
    error?: string;
    startTime?: number;
    estimatedCompletion?: number;
  } | null;
  analysisHistory: PropertyAnalysisResult[];
  exportSettings: {
    format: 'pdf' | 'json' | 'csv';
    sections: string[];
  };
  ui: {
    progressModalOpen: boolean;
    resultsTabOpen: string | null;
    exportModalOpen: boolean;
  };
}

export interface PropertyAnalysisResult {
  id: string;
  url: string;
  source: PropertySource;
  selectedTools: AnalysisToolType[];
  results: PropertyAnalysisData;
  timestamp: string;
  conversationId?: string;
}

// UI Component Props Types
export interface PropertyAnalysisProgressProps {
  analysis: PropertyAnalysisState['activeAnalysis'];
  onClose: () => void;
  onMinimize: () => void;
}

export interface PropertyAnalysisResultsProps {
  results: PropertyAnalysisData;
  onExport: (format: string, sections: string[]) => void;
  onShare: () => void;
}

export interface PropertyAnalysisTabProps {
  data: any;
  loading?: boolean;
  error?: string;
}

// Service Types
export interface PropertyServiceConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

export class PropertyAnalysisError extends Error {
  public code: string;
  public status?: number;
  public details?: any;

  constructor(message: string, code: string, status?: number, details?: any) {
    super(message);
    this.name = 'PropertyAnalysisError';
    this.code = code;
    this.status = status;
    this.details = details;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PropertyAnalysisError);
    }
  }
}

// Utility Types
export type AnalysisToolDisplayNames = {
  [K in AnalysisToolType]: string;
};

export type PropertyAnalysisExportFormat = 'pdf' | 'json' | 'csv' | 'xlsx';

export interface PropertyAnalysisExportOptions {
  format: PropertyAnalysisExportFormat;
  sections: string[];
  includeRawData: boolean;
  includeVisualizations: boolean;
}